"use client";
import React from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { motion } from "framer-motion";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import Hero from "../pageComponents/affiliate/Hero";
import ExclusiveSection from "../pageComponents/affiliate/Exclusive";
import Deals from "../pageComponents/affiliate/Deals";
import Rules from "../pageComponents/affiliate/Rules";
import bg from "@/public/home/section-bg.jpg";
import Payouts from "../pageComponents/affiliate/Payouts";
import ReviewsSection from "../pageComponents/challenge/Reviews";
import StepsSection from "../pageComponents/affiliate/Steps";
import LastCTASection from "../global/sections/LastCTA";
import Footer from "../global/partials/Footer";
import { cn } from "@/lib/utils";
import trading from "@/public/global/trading-de.png";

export default function PageAffiliateJSX({
  data,
}: {
  data: PageAffiliateSchema;
}) {
  return (
    <>
      <main className="">
        {data?.settings.header && (
          <NavBarV2 headerData={data?.settings.header} />
        )}
        <Hero data={data} />
        {data?.showReviewButton && (
          <div className="relative h-[40rem] lg:h-[47.5rem]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
              {Array.from({ length: 7 }).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-full border-l border-l-[#E5E5E5]",
                    index >= 5 && "hidden lg:block",
                  )}
                />
              ))}
            </div>

            <div className="flex absolute top-1/2 left-1/2 flex-col justify-center items-center bg-white border w-[75%] -translate-x-1/2 -translate-y-1/2 py-[5.25rem] border-[#E5E5E5] lg:w-[51.375rem] gap-[2rem] lg:gap-[4rem]">
              <div className="w-[min(16rem)]">
                <Image
                  src={trading}
                  alt=""
                  width={1000}
                  height={1000}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-[1.75rem]">
                <p className="m-0 text-[4.75rem] font-[600] font-instrument-sans text-[#103037]">
                  4,5/5
                </p>

                <svg
                  width="35"
                  height="33"
                  viewBox="0 0 35 33"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.83603 32.5031L17.0854 27.1096L27.3348 32.4973C27.3822 32.5221 27.4355 32.5332 27.4888 32.5293C27.5422 32.5254 27.5934 32.5068 27.6367 32.4754C27.68 32.4441 27.7137 32.4012 27.734 32.3518C27.7543 32.3023 27.7604 32.2482 27.7517 32.1954L25.7967 20.7817L34.0853 12.7001C34.1232 12.6626 34.15 12.6154 34.1626 12.5636C34.1753 12.5119 34.1733 12.4576 34.1569 12.4069C34.1405 12.3563 34.1103 12.3111 34.0697 12.2766C34.0292 12.242 33.9798 12.2194 33.9272 12.2113L22.4674 10.5438L17.3442 0.162182C17.3206 0.113574 17.2839 0.0725799 17.2381 0.0438964C17.1923 0.015213 17.1394 0 17.0854 0C17.0314 0 16.9785 0.015213 16.9327 0.0438964C16.887 0.0725799 16.8502 0.113574 16.8267 0.162182L11.7034 10.5467L0.24366 12.2142C0.191005 12.2223 0.141643 12.2449 0.101083 12.2794C0.060523 12.314 0.03036 12.3591 0.01396 12.4098C-0.00244005 12.4605 -0.00443232 12.5148 0.00820627 12.5665C0.0208449 12.6183 0.0476167 12.6655 0.0855343 12.7029L8.37416 20.7846L6.41628 32.1983C6.40752 32.2511 6.41364 32.3052 6.43395 32.3547C6.45426 32.4041 6.48797 32.4469 6.53127 32.4783C6.57458 32.5097 6.62577 32.5283 6.6791 32.5322C6.73243 32.536 6.78578 32.525 6.83316 32.5002L6.83603 32.5031Z"
                    fill="#6DA991"
                  />
                </svg>
              </div>

              <a
                className="flex items-center text-black capitalize transition-all duration-300 cursor-pointer font-[600] text-[1.75rem] tracking-[-.28px] gap-[1rem] hover:gap-[1.5rem] hover:brightness-110"
                href="https://trading.de/prop-trading/iq-capital/"
                target="_blank"
              >
                View reviews
                <svg
                  width="15"
                  height="13"
                  viewBox="0 0 15 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 6.20166H13M13 6.20166L7.58333 0.70166M13 6.20166L7.58333 11.7017"
                    stroke="#6DA991"
                    stroke-width="2"
                  />
                </svg>
              </a>
            </div>
          </div>
        )}
        <ExclusiveSection
          data={data}
          className={`${data?.showReviewButton ? "pb-[7rem] lg:pb-[20rem]" : "py-[7rem] lg:py-[20rem]"}`}
        />
        <Deals data={data} />
        <div
          className="relative w-full px-[2rem] mt-[2.5rem] lg:px-[5rem] lg:mt-[5rem]"
          style={{ overflowAnchor: "none" }}
        >
          <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem]">
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[#E6EDE9]">
              <Image
                className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,120rem)] !max-w-none !h-auto object-cover mix-blend-luminosity opacity-[.10]"
                style={{
                  maskImage:
                    "linear-gradient(to top, black 20%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to top, black 20%, transparent 100%)",
                }}
                src={bg}
                quality={100}
                alt="background art"
              />
            </div>
            <Rules data={data} />
          </div>
        </div>
        <Payouts data={data} />
        <div className="w-full px-[2rem]">
          <div className="bg-[#E6EDE9] rounded-[1.5rem] lg:rounded-[5rem] w-[min(230rem,100%)] mx-auto">
            <ReviewsSection
              reviewsTitle={data.reviewsTitle}
              reviewsDocument={data.reviewsDocument}
              fadeColor="green"
              slug={data?.settings?.slug?.current}
            />
          </div>
        </div>

        <StepsSection data={data} />
        <LastCTASection
          chip={data?.lastChip}
          text={data?.lastText}
          cta={data?.lastCTA}
          hidePillars={true}
        />
        {data?.settings?.footer && (
          <Footer
            data={data.settings.footer}
            slug={data?.settings?.slug?.current}
          />
        )}
      </main>
    </>
  );
}
